import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Button } from './ui/button';
import { ArrowRight, Check, X, ShieldCheck, ShieldOff } from 'lucide-react';

const comparisonData = [
  { feature: 'Nationwide legal protection', registered: true, unregistered: false },
  { feature: 'Use of registered symbol', registered: true, unregistered: false },
  { feature: 'Listed in USPTO database', registered: true, unregistered: false },
  { feature: 'Access to federal courts', registered: true, unregistered: false },
  { feature: 'Presumption of ownership', registered: true, unregistered: false },
  { feature: 'Eligible for e-commerce brand programs', registered: true, unregistered: false },
  { feature: 'Deterrent against infringers', registered: true, unregistered: false },
  { feature: 'Increases brand value as IP asset', registered: true, unregistered: false },
  { feature: 'Common law rights only', registered: false, unregistered: true },
  { feature: 'Limited to geographic area of use', registered: false, unregistered: true },
];

const ComparisonSection = ({ onOpenContactForm }) => {
  const section = useScrollAnimation({ threshold: 0.1 });
  const header = useScrollAnimation({ threshold: 0.15 });

  return (
    <section data-testid="comparison-section" className="py-24 md:py-32 relative overflow-hidden" style={{
      background: 'linear-gradient(180deg, #faf5ff 0%, #f0fdf9 50%, #faf5ff 100%)'
    }}>
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#7C3AED]/10 text-[#7C3AED] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
            COMPARE
          </span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Registered vs. Unregistered
          </h2>
          <p className="text-base text-zinc-500 leading-relaxed">
            See the difference federal trademark registration makes for your brand protection and legal standing.
          </p>
        </div>

        <div ref={section.ref} className={`max-w-3xl mx-auto scroll-fade-up scroll-delay-2 ${section.isVisible ? 'scroll-visible' : ''}`}>
          <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden shadow-lg">
            <div className="grid grid-cols-[1fr_120px_120px] sm:grid-cols-[1fr_150px_150px]">
              <div className="p-5 bg-zinc-50 border-b border-zinc-100">
                <span className="text-sm font-bold text-zinc-900">Feature</span>
              </div>
              <div className="p-5 bg-[#7C3AED]/5 border-b border-zinc-100 text-center">
                <div className="flex flex-col items-center gap-1">
                  <ShieldCheck className="w-5 h-5 text-[#7C3AED]" />
                  <span className="text-xs font-bold text-[#7C3AED]">Registered</span>
                </div>
              </div>
              <div className="p-5 bg-zinc-50 border-b border-zinc-100 text-center">
                <div className="flex flex-col items-center gap-1">
                  <ShieldOff className="w-5 h-5 text-zinc-400" />
                  <span className="text-xs font-bold text-zinc-400">Unregistered</span>
                </div>
              </div>

              {comparisonData.map((row, i) => (
                <React.Fragment key={i}>
                  <div className={`px-5 py-3.5 text-sm text-zinc-600 border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}>
                    {row.feature}
                  </div>
                  <div className={`px-5 py-3.5 flex justify-center border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}>
                    {row.registered ? (
                      <div className="w-6 h-6 rounded-full bg-[#14B8A6]/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-[#14B8A6]" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
                        <X className="w-3.5 h-3.5 text-red-400" />
                      </div>
                    )}
                  </div>
                  <div className={`px-5 py-3.5 flex justify-center border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}>
                    {row.unregistered ? (
                      <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-amber-500" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
                        <X className="w-3.5 h-3.5 text-red-400" />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <Button
              data-testid="comparison-cta"
              onClick={onOpenContactForm}
              className="bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white font-semibold px-10 h-12 rounded-full active:scale-[0.97] shadow-lg shadow-purple-500/20"
            >
              Register Your Trademark Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
